import { fromFirestoreDoc } from "@/lib/firestoreDoc";
import { db, functions } from "@/lib/firebase";
import { doc, onSnapshot, type FirestoreError, type Unsubscribe } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { buildDeferredAnalysisJobId, type DeferredAnalysisJobStatus } from "@shared/deferredAnalysis";

const DEFERRED_ANALYSIS_CALLABLE_TIMEOUT_MS = 120_000;

export type { DeferredAnalysisJobStatus };

export interface StartDeferredAnalysisJobRequest {
  taskPath: string;
  entryConfigId?: string;
  entryConfigIds?: string[];
  scope?: "task" | "entryConfig";
  includeFailed?: boolean;
}

export interface StartDeferredAnalysisJobResponse {
  jobId: string | null;
  runId: string | null;
  status: DeferredAnalysisJobStatus | "no_eligible_samples";
  totalCount: number;
  reusedExistingJob: boolean;
}

export interface DeferredAnalysisJobSnapshot {
  id: string;
  runId?: string | null;
  status?: DeferredAnalysisJobStatus;
  active?: boolean;
  totalCount?: number;
  queuedCount?: number;
  runningCount?: number;
  completedCount?: number;
  failedCount?: number;
  skippedCount?: number;
  lastError?: string;
}

export function isDeferredAnalysisJobSnapshotActive(job: DeferredAnalysisJobSnapshot | null | undefined): boolean {
  return job?.active === true && (job.status === "initializing" || job.status === "queued" || job.status === "running");
}

export function getDeferredAnalysisJobProgressText(job: DeferredAnalysisJobSnapshot | null | undefined): string | null {
  if (!isDeferredAnalysisJobSnapshotActive(job)) return null;

  const totalCount = Number(job?.totalCount ?? 0);
  if (!Number.isFinite(totalCount) || totalCount <= 0) return null;

  const rawCompletedCount = Number(job?.completedCount ?? 0);
  const rawFailedCount = Number(job?.failedCount ?? 0);
  const rawSkippedCount = Number(job?.skippedCount ?? 0);
  if (![rawCompletedCount, rawFailedCount, rawSkippedCount].every(Number.isFinite)) {
    return null;
  }

  const completedCount = Math.max(0, rawCompletedCount);
  const failedCount = Math.max(0, rawFailedCount);
  const skippedCount = Math.max(0, rawSkippedCount);
  const rawTerminalCount = completedCount + failedCount + skippedCount;
  const terminalCount = Math.min(totalCount, rawTerminalCount);
  const percentage = Math.round((terminalCount / totalCount) * 100);
  const hasIncompleteTerminalItems = failedCount > 0 || skippedCount > 0;
  const leadingLabel = hasIncompleteTerminalItems ? "processed" : "completed";
  const shouldShowBreakdown = rawTerminalCount <= totalCount;
  const details = [
    shouldShowBreakdown && hasIncompleteTerminalItems && completedCount > 0 ? `${completedCount} completed` : null,
    shouldShowBreakdown && failedCount > 0 ? `${failedCount} failed` : null,
    shouldShowBreakdown && skippedCount > 0 ? `${skippedCount} skipped` : null,
  ].filter(Boolean);

  return [`${terminalCount} of ${totalCount} ${leadingLabel} (${percentage}%)`, ...details].join(" · ");
}

export function buildOptimisticDeferredAnalysisJobSnapshot(
  response: StartDeferredAnalysisJobResponse
): DeferredAnalysisJobSnapshot | null {
  if (!response.jobId || response.status === "no_eligible_samples") {
    return null;
  }

  const active = response.status === "initializing" || response.status === "queued" || response.status === "running";

  return {
    id: response.jobId,
    runId: response.runId,
    status: response.status,
    active,
    totalCount: response.totalCount,
    queuedCount: active ? response.totalCount : 0,
    runningCount: 0,
    completedCount: 0,
    failedCount: 0,
    skippedCount: 0,
  };
}

export const isDeferredAnalysisUiEnabled = (): boolean => {
  return true;
};

export function getDeferredAnalysisJobId({
  trialId,
  experimentId,
  taskId,
  entryConfigId,
  scope = "entryConfig",
}: {
  trialId: string;
  experimentId: string;
  taskId: string;
  entryConfigId?: string | null;
  scope?: "task" | "entryConfig";
}): string {
  return buildDeferredAnalysisJobId({ trialId, experimentId, taskId, entryConfigId, scope });
}

export function subscribeToDeferredAnalysisJob(
  orgId: string,
  jobId: string,
  onChange: (job: DeferredAnalysisJobSnapshot | null) => void,
  onError?: (error: FirestoreError) => void
): Unsubscribe {
  return onSnapshot(
    doc(db, `organizations/${orgId}/analysisJobs/${jobId}`),
    (snapshot) => {
      onChange(snapshot.exists() ? fromFirestoreDoc<DeferredAnalysisJobSnapshot>(snapshot) : null);
    },
    (error) => {
      onError?.(error);
    }
  );
}

export async function startDeferredAnalysisJob(
  request: StartDeferredAnalysisJobRequest
): Promise<StartDeferredAnalysisJobResponse> {
  const callable = httpsCallable<StartDeferredAnalysisJobRequest, StartDeferredAnalysisJobResponse>(
    functions,
    "startDeferredAnalysisJob",
    { timeout: DEFERRED_ANALYSIS_CALLABLE_TIMEOUT_MS }
  );
  const result = await callable(request);
  return result.data;
}
